import { encodeFunctionData } from "viem";
import { hybridEscrowAbi } from "./hybridEscrowAbi.js";
import { erc20Abi } from "./erc20Abi.js";
import { erc721Abi } from "./erc721Abi.js";
export { hybridEscrowAbi } from "./hybridEscrowAbi.js";
export { erc20Abi } from "./erc20Abi.js";
export { erc721Abi } from "./erc721Abi.js";
export function encodeApproveCall(usdcAddress, spender, amount) {
    return {
        to: usdcAddress,
        data: encodeFunctionData({ abi: erc20Abi, functionName: "approve", args: [spender, amount] }),
        value: 0n,
    };
}
export function encodeFundEscrowCall(escrowAddress, args) {
    return {
        to: escrowAddress,
        data: encodeFunctionData({
            abi: hybridEscrowAbi,
            functionName: "fundEscrow",
            args: [args.orderId, args.seller, args.principalAmount, args.disputeDeadline],
        }),
        value: 0n,
    };
}
export function encodeReleaseCall(escrowAddress, orderId) {
    return {
        to: escrowAddress,
        data: encodeFunctionData({ abi: hybridEscrowAbi, functionName: "release", args: [orderId] }),
        value: 0n,
    };
}
export function encodeClaimAfterDeadlineCall(escrowAddress, orderId) {
    return {
        to: escrowAddress,
        data: encodeFunctionData({
            abi: hybridEscrowAbi,
            functionName: "claimAfterDeadline",
            args: [orderId],
        }),
        value: 0n,
    };
}
export function encodeOpenDisputeCall(escrowAddress, orderId, evidenceHash) {
    return {
        to: escrowAddress,
        data: encodeFunctionData({
            abi: hybridEscrowAbi,
            functionName: "openDispute",
            args: [orderId, evidenceHash],
        }),
        value: 0n,
    };
}
/**
 * Arbitrator-only. `buyerAmount + sellerAmount` must exactly equal the escrow's principal —
 * see `HybridEscrow.sol`'s `resolveDispute` docblock. Never called by the API itself; this
 * exists for arbitration tooling (`scripts/resolve-dispute.ts`) that submits the transaction
 * directly with the arbitrator's own key/Safe, entirely outside the web app's trust boundary.
 */
export function encodeResolveDisputeCall(escrowAddress, args) {
    return {
        to: escrowAddress,
        data: encodeFunctionData({
            abi: hybridEscrowAbi,
            functionName: "resolveDispute",
            args: [args.orderId, args.buyerAmount, args.sellerAmount, args.resolutionHash],
        }),
        value: 0n,
    };
}
/**
 * Pays out `account`'s full `withdrawable` pull-balance to `account`. Permissionless — callable
 * by anyone, but funds always land on `account` regardless of who submits the transaction. Only
 * has a nonzero balance to pay if a prior direct payout to `account` failed (e.g. it was
 * blacklisted at the time) — see `HybridEscrow.sol`'s `_payOut`/`withdraw` docblocks.
 */
export function encodeWithdrawCall(escrowAddress, account) {
    return {
        to: escrowAddress,
        data: encodeFunctionData({ abi: hybridEscrowAbi, functionName: "withdraw", args: [account] }),
        value: 0n,
    };
}
/** Reads how much USDC `account` currently has credited but not yet withdrawn. Normally zero. */
export async function readOnchainWithdrawable(client, escrowAddress, account) {
    return client.readContract({
        address: escrowAddress,
        abi: hybridEscrowAbi,
        functionName: "withdrawable",
        args: [account],
    });
}
/** Reads the contract's own fee quote so the API can cross-check its local computation. */
export async function readOnchainQuote(client, escrowAddress, principal) {
    const [platformFee, totalDue] = await client.readContract({
        address: escrowAddress,
        abi: hybridEscrowAbi,
        functionName: "quote",
        args: [principal],
    });
    return { platformFee, totalDue };
}
const STATE_NAMES = ["NONE", "FUNDED", "DISPUTED", "RELEASED", "REFUNDED"];
export async function readOnchainEscrow(client, escrowAddress, orderId) {
    const raw = await client.readContract({
        address: escrowAddress,
        abi: hybridEscrowAbi,
        functionName: "getEscrow",
        args: [orderId],
    });
    return {
        buyer: raw.buyer,
        seller: raw.seller,
        principal: raw.principal,
        platformFeeBpsUsed: raw.platformFeeBpsUsed,
        platformFee: raw.platformFee,
        totalCharged: raw.totalCharged,
        treasuryAtFunding: raw.treasuryAtFunding,
        fundedAt: raw.fundedAt,
        disputeDeadline: raw.disputeDeadline,
        state: STATE_NAMES[raw.state] ?? "NONE",
    };
}
/**
 * Reads an ERC721 token's current owner — the only on-chain primitive Phase 5a' (NFT_TRANSFER
 * fulfillment) needs. Used both at listing-time (does the seller's own wallet actually hold this
 * token right now?) and at delivery-check time (has it since moved to the buyer's wallet?). A
 * live read has no propagation delay, unlike lib/domainVerification.ts's DNS lookup — there's
 * nothing to wait on. Returns null (never throws) for a token that doesn't exist / was burned /
 * the contract call otherwise reverts — from the caller's point of view "no such token" and "not
 * owned by who we expected" are both just "not verified yet".
 */
export async function readNftOwner(client, contractAddress, tokenId) {
    try {
        return await client.readContract({
            address: contractAddress,
            abi: erc721Abi,
            functionName: "ownerOf",
            args: [tokenId],
        });
    }
    catch {
        return null;
    }
}
export class DeploymentValidationError extends Error {
}
/**
 * Startup check (called by apps/api, apps/chain-worker) that the configured escrow/USDC
 * addresses actually contain bytecode and that the escrow contract reports the expected
 * USDC token and platform fee. Prevents booting against a misconfigured or empty address.
 */
export async function validateDeployment(client, params) {
    const [escrowCode, usdcCode] = await Promise.all([
        client.getCode({ address: params.escrowAddress }),
        client.getCode({ address: params.usdcAddress }),
    ]);
    if (!escrowCode || escrowCode === "0x") {
        throw new DeploymentValidationError(`No bytecode found at ESCROW_CONTRACT_ADDRESS ${params.escrowAddress}`);
    }
    if (!usdcCode || usdcCode === "0x") {
        throw new DeploymentValidationError(`No bytecode found at USDC_CONTRACT_ADDRESS ${params.usdcAddress}`);
    }
    const [onchainUsdc, onchainFeeBps] = await Promise.all([
        client.readContract({
            address: params.escrowAddress,
            abi: hybridEscrowAbi,
            functionName: "USDC",
        }),
        client.readContract({
            address: params.escrowAddress,
            abi: hybridEscrowAbi,
            functionName: "PLATFORM_FEE_BPS",
        }),
    ]);
    if (onchainUsdc.toLowerCase() !== params.usdcAddress.toLowerCase()) {
        throw new DeploymentValidationError(`Escrow contract's configured USDC (${onchainUsdc}) does not match USDC_CONTRACT_ADDRESS (${params.usdcAddress})`);
    }
    if (onchainFeeBps !== params.expectedPlatformFeeBps) {
        throw new DeploymentValidationError(`Escrow contract's PLATFORM_FEE_BPS (${onchainFeeBps}) does not match configured PLATFORM_FEE_BPS (${params.expectedPlatformFeeBps})`);
    }
}
//# sourceMappingURL=contract.js.map